import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { type PixelSize } from '@/features/shapes/lib/graphic-resolution'

/**
 * What "blurs past 1.2× zoom" means, behind an icon.
 *
 * Andrew, 2026-09-23: "The add graphic modal is getting too tall… If it is saying it will be
 * blurred, have a information icon next to it that displays the text we are removing." The size a
 * graphic draws at and the reason it draws at that size were two permanent lines in a dialog that
 * already had six fields; they only matter when something is wrong with the file, and then they
 * matter a lot — the fix is a bigger image, not a different size, and nothing else says so.
 *
 * Shown ONLY beside the blur warning, so the good case stays one green line.
 */
export function SizeInfo({ box, needed, zoom }: { box: PixelSize; needed: PixelSize; zoom: number }) {
    const { t, i18n } = useTranslation()
    const formatted = (Math.floor(zoom * 10) / 10).toLocaleString(i18n.language, { maximumFractionDigits: 1 })

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        // A button, not a bare icon: a tooltip that only opens on hover is invisible
                        // to anyone using the keyboard, and this is the only place the remedy is written.
                        className="inline-flex shrink-0 rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        aria-label={t('shapes.sizeInfoLabel', 'About this size')}
                        onClick={(e) => e.preventDefault()}
                    >
                        <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs space-y-1.5 text-xs" data-testid="graphic-size-info">
                    <p className="tabular-nums">
                        {t('shapes.drawnSizeLabel', 'Size on the canvas')} · {box.width} × {box.height} px
                    </p>
                    <p className="text-muted-foreground">
                        {t('shapes.drawnSizeStandard', 'Set to 2 cm high, the standard for graphics. The file can be any resolution: larger stays sharper when zoomed.')}
                    </p>
                    <p className="text-muted-foreground">
                        {t('shapes.blursPastZoom',
                            'Blurs when zoomed in past {{zoom}}×. For a graphic that stays sharp, use a larger image: at least {{width}} × {{height}} px. It will still draw at this size.',
                            { zoom: formatted, width: needed.width, height: needed.height })}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
